import { lazy, Suspense, useMemo, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Card from '../common/components/Card';
import ConfigStatusButton from '../common/components/ConfigStatusButton';
import CustomForm from '../common/components/CustomForm';
import { FloatingLabel } from '../common/components/FloatingLabel';
import { Col, Row } from '../common/components/Grid';
import Icon from '../common/components/Icon';
import { ResizeAwareDiv } from '../common/components/ResizeablePanel';
import { SettingsDisplay, useSettingsContext } from '../common/components/SettingsPanels';
import useClientConfig from '../common/hooks/clientConfigHook';
import useConfigStatuses from '../common/hooks/configStatusHook';
import { useUpsertSetting } from '../common/hooks/settingsHooks';
import { deepClone, deepMerge } from '../common/utils/objectUtilities';
import PropTypes from 'prop-types';
import TextInput from '../common/components/TextInput';
import OfflineMask from '../common/components/OfflineMask';
import { checkEnabled } from '../utils/enableUtilities';

const ConfigDetails = ({ label, onClose }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const { upsertSetting } = useUpsertSetting();
    const { popPanel } = useSettingsContext()
    const def = useMemo(() => [
        {
            title: t('notes'),
            rows: [
                [
                    {
                        type: "richtext",
                        colspan: 12,
                        field: "notes",
                        label: " "
                    }
                ]
            ]
        },
        {
            title: t('taskList_one'),
            rows: [
                [
                    {
                        type: "table",
                        colspan: 12,
                        field: "tasks",
                        columns: [
                            { type: "date", field: "date", title: t('dateTime'), label: t('dateTime'), required: true },
                            { type: "text", field: "details", title: t('details'), label: t('details'), required: true },
                            { type: "text", field: "owner", title: t('owner'), label: t('owner') },
                            { type: "checkbox", field: "complete", title: t('complete') }
                        ]
                    }
                ]
            ]
        }
        // {
        //     title: t('taskList_one'),
        //     rows: [
        //         [
        //             {
        //                 type: "list",
        //                 colspan: 12,
        //                 field: "tasks",
        //                 rowdef: [
        //                     {
        //                         borderless: true,
        //                         rows: [
        //                             [
        //                                 {
        //                                     type: "date",
        //                                     field: "date",
        //                                     label: "Date",
        //                                     colspan: 2,
        //                                     required: true
        //                                 },
        //                                 {
        //                                     type: "text",
        //                                     field: "details",
        //                                     label: "Description",
        //                                     colspan: 6,
        //                                     required: true
        //                                 },
        //                                 {
        //                                     type: "text",
        //                                     field: "owner",
        //                                     label: "Owner",
        //                                     colspan: 3,
        //                                     required: true
        //                                 },
        //                                 {
        //                                     type: "checkbox",
        //                                     field: "complete",
        //                                     label: " ",
        //                                     colspan: 1
        //                                 }
        //                             ]
        //                         ]
        //                     }
        //                 ]
        //             }
        //         ]
        //     ]
        // }
    ], [t])
    const components = useMemo(() => componentFactory(t, clientConfig), [t, clientConfig])

    return <SettingsDisplay title={label} controls={<Row>
        <Col></Col>
        <Col xs="auto">
            <Button variant="success" confirmMessage={t('confirmConfigComplete')} onClick={() => { upsertSetting(null, 'configDetails', { ...clientConfig.configDetails, complete: true }); popPanel(); }}><Icon intent="check"></Icon> {t('complete')}</Button>
        </Col>
    </Row>} onClose={onClose}>
        <div className="ml-2 mr-0 h-100 d-flex flex-column">
            <div className="mt-2" style={{ flex: 1, overflowY: 'auto' }}>
            <Card title={t('status_one')} bodyStyle={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, 200px)'}}>
                {components.filter(c => c.cond !== false && c.hideStatus !== true).map(c => <FloatingLabel key={c.uuid} value={true} placeholder={c.label}>
                    <ConfigStatusButton uuid={c.uuid} style={{ width: '100%' }}></ConfigStatusButton>
                </FloatingLabel>)}
            </Card>
            <CustomForm def={def} data={clientConfig.configDetails ?? {}} onChange={delta => upsertSetting(null, 'configDetails', deepMerge(deepClone(clientConfig.configDetails)), delta)}></CustomForm>
            </div>
        </div>
    </SettingsDisplay>
}

const componentFactory = (t, clientConfig) => [
    { uuid: 'd3cc9a58-5735-4dea-91c2-4df7b8aca1cb', Component: lazy(() => Promise.resolve({ default: ConfigDetails})), label: t('status_one'), hideStatus: true, cond: !clientConfig.configDetails?.complete },
    { uuid: '0a897d73-1843-46d1-8cd1-78ddb7e15fa1', Component: lazy(() => import('./settings/AccountRequests')), label: t('accountRequest_other'), cond: clientConfig.accountRequest?.enable },
    { uuid: '8dfe1718-bc70-4f6b-8a84-51c0d58f7db1', Component: lazy(() => import('./settings/Users')), label: t('user_other') },
    { uuid: '18eeeee8-7dba-443c-a5f6-ce4f350ef4f9', Component: lazy(() => import('./settings/Site')), label: t('site_other'), cond: clientConfig.enableSites },
    { uuid: 'ea88220d-2b40-4ed5-a4fe-8f3e7c8378c0', Component: lazy(() => import('./settings/Level')), label: t('level_other'), cond: clientConfig.enableLevels },
    { uuid: 'd409ffde-146b-446b-b305-6c2db360b0e9', Component: lazy(() => import('./settings/Events')), label: t('event_other'), cond: clientConfig.enableEvents === true && !clientConfig.trackerOnly },
    { uuid: 'a8be2fdd-8e42-44a7-95da-44c1cd233997', Component: lazy(() => import('./settings/Types')), label: t('type_other') },
    { uuid: 'c3dc026b-0c07-4a5c-a1ee-f78fd9200bb3', Component: lazy(() => import('./settings/Problems')), label: t('problem_other'), cond: !clientConfig.trackerOnly },
    { uuid: 'e5a4b3d8-3e7f-44ae-b14d-e3caad930203', Component: lazy(() => import('./settings/Priorities')), label: t('priority_other'), cond: !clientConfig.trackerOnly },
    { uuid: '353cceaf-a1d5-4738-9eff-633d55c602b7', Component: lazy(() => import('./settings/Results')), label: t('result_other'), cond: clientConfig.enableResult },
    { uuid: 'e2af5cc3-bdbd-4190-4a23-21ab67ae97d2', Component: lazy(() => import('./settings/JobStatuses')), label: t('jobStatus_other') },
    { uuid: 'a79eaa41-c035-4576-9f95-1f1f32287ece', Component: lazy(() => import('./settings/Locations')), label: t('location_other') },
    { uuid: '482ff725-9e5a-4c8f-aefd-5e5a80f3ee04', Component: lazy(() => import('./settings/MapOverlays')), label: t('overlay_other'), cond: clientConfig.enableMapping },
    { uuid: '35445b6f-a17a-4a3f-b245-e8f37ff363dd', Component: lazy(() => import('./settings/JobTags')), label: t('jobTag_other'), cond: checkEnabled(clientConfig, clientConfig.enableJobTags) && !clientConfig.trackerOnly },
    { uuid: 'facb6f93-0b37-42f2-994b-268762dc2e4f', Component: lazy(() => import('./settings/SOPs')), label: t('sop_other'), cond: !clientConfig.trackerOnly },
    { uuid: 'bcd2c06b-03d7-41b5-96c3-229267c8ad6e', Component: lazy(() => import('./settings/RequiredComments')), label: t('requiredComment_other'), cond: !clientConfig.trackerOnly },
    { uuid: '6ebef393-fd82-4379-9562-f81673f662e1', Component: lazy(async () => ({ default: (await import('./settings/CustomForms')).JobCustomForms })), label: t('jobFormDefs'), cond: checkEnabled(clientConfig, clientConfig.enableJobForms) && !clientConfig.trackerOnly},
    { uuid: 'd4a3990e-1854-45ac-af12-899c110c2781', Component: lazy(async () => ({ default: (await import('./settings/CustomForms')).CrewCustomForms })), label: t('crewFormDefs'), cond: clientConfig.enableCrews && clientConfig.enableCrewForms },
    { uuid: '9e40a569-1fc5-4256-8497-8febdfb34e9f', Component: lazy(() => import('./settings/ReferenceDocs')), label: t('referenceDocument_other'), cond: clientConfig.enableRefDocs },
    { uuid: '0c534807-70d5-4a8c-b4ce-089aef7bde43', Component: lazy(() => import('./settings/Glossary')), label: t('glossary'), cond: clientConfig.enableGlossary && !clientConfig.trackerOnly },
    { uuid: '743ac4ee-89fe-48b3-9851-6293397971e7', Component: lazy(() => import('./settings/Lists')), label: t('knowledgeBase'), cond: clientConfig.enableLists && !clientConfig.trackerOnly },
    { uuid: 'b5699c35-978d-49d4-a3b5-82a6286851a6', Component: lazy(() => import('./settings/TaskLists')), label: t('taskList_other'), cond: clientConfig.enableTaskLists && !clientConfig.trackerOnly },
    { uuid: 'c55ffd1f-30f2-4ac4-89ef-ab443b5578c7', Component: lazy(() => import('./settings/Contacts')), label: t('contact_other') },
    { uuid: 'b768552e-570f-448e-be8b-604a9c50f5b3', Component: lazy(() => import('./settings/ContactLists')), label: t('contactList_other') },
    { uuid: '5c07eb94-73ac-4eac-b77d-954ee381dce6', Component: lazy(() => import('./settings/NotificationRules')), label: t('notificationRule_other'), cond: !clientConfig.trackerOnly },
    { uuid: '1c5f93cf-7b31-4ba6-9a29-d78f6d28ec8b', Component: lazy(() => import('./settings/StandardReplies')), label: t('stdReplies'), cond: clientConfig.enableStdReplies && !clientConfig.trackerOnly },
    { uuid: '323ab1f4-1827-45c2-aea8-aaa169104ff1', Component: lazy(() => import('./settings/OutsideAgencies')), label: t('outsideAgency_other'), cond: clientConfig.enableOutsideAgencies },
    { uuid: 'e13b5917-c52f-4a1a-be73-f0feafdb53e7', Component: lazy(() => import('./settings/CrewKinds')), label: t('crewKind_other') },
    { uuid: 'ff45acd3-2add-5119-4a2e-98a2e4ffad2c', Component: lazy(() => import('./settings/CrewStatuses')), label: t('crewStatus_other') },
    { uuid: 'e9d9ac4e-0dd0-4ada-acd7-ff9ecbf0bf82', Component: lazy(() => import('./settings/CrewResources')), label: t('crewResource_other'), cond: clientConfig.enableCrews },
    { uuid: '78dfbb5a-2a49-4def-9d1b-415ae7caef5e', Component: lazy(() => import('./settings/GeoFencing')), label: t('geofencing'), cond: clientConfig.enableCrews },
    { uuid: 'e12d2f41-f473-47a7-9cc8-3ed0077e01e1', Component: lazy(() => import('./settings/DocTypes')), label: t('docType_other'), cond: !clientConfig.trackerOnly },
    { uuid: '017428e5-eddf-4320-8c16-38af3c18083d', Component: lazy(() => import('./settings/SitRepDef')), label: t('sitrep'), cond: clientConfig.sitrep?.enable },
    { uuid: '7e59b96c-a41d-4a66-b5b9-3fa49807e343', Component: lazy(() => import('./settings/Exports')), label: t('export') },
]



const ComponentButton = ({ e, selected, setEntry, showStatus = true }) => {
    const { pushPanel } = useSettingsContext();
    const clientConfig = useClientConfig();
    const statuses = useConfigStatuses();
    const currentStatus = statuses.find(s => s.value === clientConfig.configStatus?.[e.uuid]);

    return <Button id={`settings_${e.uuid}`} key={'settings_btn_' + e.label} className="mx-3 my-1 text-left p-0" aria-label={e.label}
        variant={selected ? "primary" : "outline-primary"} onClick={() => { pushPanel(1, () => setEntry()); setEntry(e.uuid); }}>
            <Row>
                <Col className="p-2" xs>
                    {e.label}
                </Col>
                { showStatus && <Col xs="auto" style={{ backgroundColor: currentStatus?.color, width: '25px' }}>
                </Col> }
            </Row>
        </Button>
}

ComponentButton.propTypes = {
    e: PropTypes.object,
    selected: PropTypes.bool,
    setEntry: PropTypes.func,
    showStatus: PropTypes.bool
}

const ComponentDisplay = ({ entry }) => {
    const { popPanel } = useSettingsContext();
    return <Suspense fallback={<Spinner></Spinner>}>
        <entry.Component uuid={entry.uuid} label={entry.label} onClose={() => popPanel()}></entry.Component>\
        <OfflineMask></OfflineMask>
    </Suspense>
}
ComponentDisplay.propTypes = {
    entry: PropTypes.object
}

const SettingsImpl = () => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const [ entry, setEntry ] = useState('8dfe1718-bc70-4f6b-8a84-51c0d58f7db1');
    const [ filter, setFilter ] = useState();
    const components = useMemo(() => componentFactory(t, clientConfig), [t, clientConfig]);

    const selectedEntry = components.find(c => c.uuid === entry);

    return <div style={{height: 'calc(100dvh - 60px)'}}><ResizeAwareDiv>
        <SettingsDisplay maxSizes={[250, Infinity]}>
            <div className='h-100' style={{ display: 'flex', flexDirection: 'column', backgroundColor: 'lightgrey' }}>
                <div className="py-1 px-3" style={{ position: 'sticky', top: 0, backgroundColor: 'lightgrey' }}>
                    <TextInput hideLabel prependText={<Icon intent="filter"></Icon>} placeholder={t('filter')} value={filter} live onChange={setFilter}></TextInput>
                </div>
                <div className="py-2" style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1 }}>
                    {components.filter(e => e.cond !== false && (!filter || e.label.toUpperCase().includes(filter.toUpperCase()))).map(e => <ComponentButton key={e.uuid} e={e} selected={e.uuid === entry} setEntry={setEntry} showStatus={!e.hideStatus && !clientConfig.configDetails?.complete}></ComponentButton>)}
                </div>
            </div>
            { selectedEntry ? <ComponentDisplay entry={selectedEntry}></ComponentDisplay> : null }
        </SettingsDisplay>
    </ResizeAwareDiv>
    </div>
}

export const AngularSettings = () => {
    return <div style={{height: 'calc(100dvh - 60px)'}}>
        <SettingsImpl></SettingsImpl>
    </div>
}

export const Settings = () => <SettingsImpl></SettingsImpl>